<script lang="ts">
  import { Eye, EyeOff, AlertCircle, ShieldCheck, Copy, Check } from 'lucide-svelte';
  import {
    login as apiLogin,
    loginVerify,
    twoFASetup,
    twoFAEnable,
    type TwoFASetup
  } from '$lib/api/auth';
  import { getErrorMessage } from '$lib/api/client';

  const { onAuthenticated }: { onAuthenticated: () => void } = $props();

  type Step = 'password' | 'code' | 'setup' | 'recovery';

  let step = $state<Step>('password');
  let email = $state('');
  let password = $state('');
  let showPassword = $state(false);
  let loading = $state(false);
  let error = $state('');

  let mfaToken = $state('');
  let code = $state('');
  let setup = $state<TwoFASetup | null>(null);
  let recoveryCodes = $state<string[]>([]);
  let recoverySaved = $state(false);
  let copied = $state(false);

  async function submitPassword(e: SubmitEvent) {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      error = 'Email and password are required';
      return;
    }
    loading = true;
    error = '';
    try {
      const challenge = await apiLogin(email.trim(), password);
      mfaToken = challenge.mfa_token;
      code = '';
      if (challenge.status === 'mfa_required') {
        step = 'code';
      } else {
        setup = await twoFASetup(mfaToken);
        step = 'setup';
      }
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      loading = false;
    }
  }

  async function submitCode(e: SubmitEvent) {
    e.preventDefault();
    if (code.trim().length < 6) {
      error = 'Enter the 6-digit code from your authenticator app, or a recovery code';
      return;
    }
    loading = true;
    error = '';
    try {
      await loginVerify(mfaToken, code.trim());
      onAuthenticated();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      loading = false;
    }
  }

  async function submitEnable(e: SubmitEvent) {
    e.preventDefault();
    if (code.trim().length < 6) {
      error = 'Enter the 6-digit code shown in your authenticator app';
      return;
    }
    loading = true;
    error = '';
    try {
      const data = await twoFAEnable(mfaToken, code.trim());
      recoveryCodes = data.recovery_codes ?? [];
      step = 'recovery';
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      loading = false;
    }
  }

  async function copyRecovery() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'));
      copied = true;
      setTimeout(() => (copied = false), 2000);
    } catch {
      /* clipboard may be unavailable; codes are visible on screen */
    }
  }
</script>

<!-- `auth-screen` locks this pre-auth screen to the light palette regardless
     of the app's theme setting (see app.css) — its brand panel is already a
     fixed gradient/literal-white design (matched to toyama_partner_portal),
     so letting the shared --tym-navy/--tym-white etc. tokens invert here
     would just break the fixed .auth-field-input/.auth-btn-* pairings. -->
<div class="auth-screen min-h-screen grid md:grid-cols-[1.05fr_.95fr] font-body">
  <!-- ── Brand panel (desktop only) ──────────────────────────────────── -->
  <div class="hidden md:flex relative bg-grad-hero overflow-hidden p-14 flex-col justify-between text-[#FFFFFF]">
    <div
      class="absolute inset-0 pointer-events-none"
      style="background:radial-gradient(circle at 18% 12%,rgba(151,215,0,.16) 0%,transparent 42%),radial-gradient(circle at 90% 100%,rgba(34,106,115,.55) 0%,transparent 50%)"
    ></div>
    <div class="relative">
      <img src="/logo.svg" class="h-6" alt="Toyama" />
    </div>
    <div class="relative max-w-md">
      <p class="font-semibold text-[13px] tracking-[.12em] uppercase text-toyama-lime mb-[18px]">
        ProQuote
      </p>
      <h1 class="font-heading font-bold text-[44px] leading-[1.08] tracking-tight mb-[18px]">
        From BOQ<br />to approved quote.
      </h1>
      <p class="font-light text-[17px] leading-relaxed text-[#FFFFFF]/75">
        Designers build the BOQ, admins price and approve it, and clients receive a branded PDF
        quote — all in one place.
      </p>
    </div>
    <div class="relative font-light text-[13px] text-[#FFFFFF]/60">
      Staff access only &middot; protected by two-step verification.
    </div>
  </div>

  <!-- ── Form panel ──────────────────────────────────────────────────── -->
  <div class="flex flex-col bg-[#FFFFFF]">
    <!-- Fixed --tym-navy-800 (not adaptive `bg-navy`): this auth screen's
         brand chrome is deliberately fixed regardless of theme (it already
         hardcodes bg-[#FFFFFF] above, not the adaptive `bg-white`), and both
         spots pair navy with literal white text that must stay legible. -->
    <div class="md:hidden flex items-center px-6 py-4 bg-[var(--tym-navy-800)]">
      <img src="/logo.svg" class="h-5" alt="Toyama" />
    </div>

    <div class="flex-1 flex items-center justify-center px-8 py-12">
      <div class="w-full max-w-[380px] auth-fade-in">
        <div
          class="inline-flex items-center gap-2 bg-[var(--tym-navy-800)] text-[#FFFFFF] px-3 py-1.5 rounded-pill text-[11px] font-semibold tracking-[.06em] uppercase mb-5"
        >
          <ShieldCheck class="w-3.5 h-3.5 text-toyama-lime shrink-0" />
          ProQuote
        </div>

        {#if step === 'password'}
          <h2 class="font-heading font-bold text-[28px] tracking-tight text-navy mb-2">
            Internal sign in
          </h2>
          <p class="font-light text-[15px] leading-relaxed text-dark-gray mb-8">
            ProQuote &middot; staff access only.
          </p>

          <form class="space-y-5" onsubmit={submitPassword}>
            <div>
              <label class="auth-field-label" for="email">Email</label>
              <input
                id="email"
                type="email"
                required
                autocomplete="username"
                bind:value={email}
                class="auth-field-input"
                placeholder="you@toyama.com"
              />
            </div>
            <div>
              <label class="auth-field-label" for="password">Password</label>
              <div class="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autocomplete="current-password"
                  bind:value={password}
                  class="auth-field-input pr-10"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-dark-gray"
                  onclick={() => (showPassword = !showPassword)}
                  aria-label="Toggle password visibility"
                >
                  {#if showPassword}<EyeOff class="h-5 w-5" />{:else}<Eye class="h-5 w-5" />{/if}
                </button>
              </div>
            </div>

            {#if error}
              <div class="rounded-lg border border-[#A32D2D]/30 bg-[#A32D2D]/5 px-4 py-3 flex gap-2">
                <AlertCircle class="h-4 w-4 text-[#A32D2D] shrink-0 mt-0.5" />
                <p class="text-[13px] text-[#A32D2D]">{error}</p>
              </div>
            {/if}

            <button type="submit" disabled={loading} class="auth-btn-accent w-full">
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        {:else if step === 'code'}
          <h2 class="font-heading font-bold text-[28px] tracking-tight text-navy mb-2">
            Two-step verification
          </h2>
          <p class="font-light text-[15px] leading-relaxed text-dark-gray mb-7">
            Enter the 6-digit code from your authenticator app, or a recovery code.
          </p>

          <div class="auth-card p-7">
            <form class="space-y-5" onsubmit={submitCode}>
              <div class="flex justify-center text-toyama-teal">
                <ShieldCheck class="h-10 w-10" />
              </div>
              <div>
                <label class="auth-field-label" for="code">Verification code</label>
                <input
                  id="code"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  bind:value={code}
                  placeholder="123456"
                  class="auth-field-input text-center text-lg tracking-[.4em] font-mono"
                />
              </div>
              {#if error}
                <p class="text-[13px] text-[#A32D2D] text-center">{error}</p>
              {/if}
              <button type="submit" disabled={loading} class="auth-btn-accent w-full">
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </form>
          </div>
        {:else if step === 'setup' && setup}
          <h2 class="font-heading font-bold text-[28px] tracking-tight text-navy mb-2">
            Set up two-step verification
          </h2>
          <p class="font-light text-[15px] leading-relaxed text-dark-gray mb-7">
            Two-step verification is required for staff accounts. It takes about a minute.
          </p>

          <div class="auth-card p-7 space-y-6">
            <p class="text-[13px] text-toyama-heavy-carbon font-light">
              Scan this QR code with an authenticator app such as Google Authenticator, Microsoft
              Authenticator, or 1Password.
            </p>
            <div class="flex justify-center">
              <img
                src={setup.qr}
                alt="Authenticator QR code"
                class="rounded-lg border border-toyama-cloudy-gray p-3 w-44 h-44"
              />
            </div>
            <details class="text-[13px]">
              <summary class="cursor-pointer text-toyama-teal hover:text-navy font-medium">
                Can't scan? Enter the key manually
              </summary>
              <p class="mt-2 text-toyama-heavy-carbon opacity-80">
                Enter this key in your authenticator app:
              </p>
              <code
                class="mt-1 block break-all rounded bg-toyama-light-carbon border border-toyama-cloudy-gray px-3 py-2 font-mono text-xs text-navy select-all"
                >{setup.secret}</code
              >
            </details>
            <form class="space-y-4 pt-2 border-t border-toyama-cloudy-gray" onsubmit={submitEnable}>
              <div>
                <label class="auth-field-label" for="setup-code"
                  >Enter the 6-digit code from the app</label
                >
                <input
                  id="setup-code"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  bind:value={code}
                  placeholder="123456"
                  class="auth-field-input text-center text-lg tracking-[.4em] font-mono"
                />
              </div>
              {#if error}
                <p class="text-[13px] text-[#A32D2D] text-center">{error}</p>
              {/if}
              <button type="submit" disabled={loading} class="auth-btn-accent w-full">
                {loading ? 'Enabling...' : 'Turn on two-step verification'}
              </button>
            </form>
          </div>
        {:else if step === 'recovery'}
          <div
            class="inline-flex items-center gap-2 bg-toyama-lime/20 text-toyama-teal px-3 py-1.5 rounded-pill text-[11px] font-semibold tracking-[.06em] uppercase mb-5"
          >
            Two-step verification is on
          </div>
          <h2 class="font-heading font-bold text-[28px] tracking-tight text-navy mb-2">
            Save your recovery codes
          </h2>
          <p class="font-light text-[15px] leading-relaxed text-dark-gray mb-7">
            Store these somewhere safe. Each one can be used once to sign in if you lose access to
            your authenticator app. They are shown only this once.
          </p>

          <div class="auth-card p-7 space-y-6">
            <div class="rounded-lg border border-toyama-cloudy-gray bg-toyama-light-carbon p-4">
              <div class="grid grid-cols-2 gap-x-6 gap-y-2 font-mono text-sm text-navy select-all">
                {#each recoveryCodes as rc (rc)}
                  <span class="tracking-wide">{rc}</span>
                {/each}
              </div>
            </div>

            <button type="button" onclick={copyRecovery} class="auth-btn-secondary w-full">
              {#if copied}<Check class="h-4 w-4" /> Copied{:else}<Copy class="h-4 w-4" /> Copy codes{/if}
            </button>

            <label class="flex items-center gap-2 text-[13px] text-toyama-heavy-carbon cursor-pointer select-none">
              <input
                type="checkbox"
                bind:checked={recoverySaved}
                class="w-4 h-4 rounded border-toyama-cloudy-gray accent-toyama-teal"
              />
              I've saved my recovery codes
            </label>

            <button
              type="button"
              disabled={!recoverySaved}
              onclick={onAuthenticated}
              class="auth-btn-accent w-full"
            >
              Continue to dashboard
            </button>
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>
